/**
 * What a guarded route does about whoever is standing on it.
 *
 * Kept apart from the component because it is the rule, and this rule has
 * already been wrong once in a way nothing could catch: on the web the guard
 * ran `history.push(document.referrer)`, so every guarded route — the
 * profile, the wallets, the orders, the tracker — mounted and threw the
 * visitor straight back where they came from. No error, no redirect anybody
 * could see, just a sidebar that did nothing when clicked.
 *
 * The front door above this decides what a signed-out visitor sees
 * (`chooseDoor`). By the time a guarded route renders, that question has been
 * answered, so there is nothing left for this to do but wait or, in the app,
 * send them to the app's own sign-in screen.
 */
export type Guard =
  /** Show the route. */
  | 'render'
  /** The app's sign-in screen, which only the desktop app has. */
  | 'sign-in'
  /** Render nothing and push nobody anywhere. */
  | 'wait'

export interface GuardState {
  /** Signed in, and of a status this route accepts. */
  isAllowed: boolean
  /** The desktop app, or a browser standing in for it. */
  isApp: boolean
  /** Local development, where every route is open. */
  isDev: boolean
}

export const chooseGuard = ({ isAllowed, isApp, isDev }: GuardState): Guard => {
  if (isAllowed) {
    return 'render'
  }

  if (isDev) {
    return 'wait'
  }

  // A website has no sign-in screen of its own: it renders nothing and
  // navigates back here, and the two would push the router at each other
  // until React gives up.
  return isApp ? 'sign-in' : 'wait'
}
